import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PlatformUserAccountService } from '@data/_generated-api/platform-api/services/platform-user-account.service';
import { UpamErrorMapper } from '@data/_generated-api/error-mappers/upam-error-mapper';
import { AsyncResult } from '@common/result/asyncResult';
import { ok } from '@common/result/Result';
import { PlatformRegisterUserAccount } from '@data/_generated-api/platform-api/models/platform-register-user-account';
import { PlatformAccountCreatedByMethod } from '@data/_generated-api/platform-api/models/platform-account-created-by-method';

export interface PublicRegistration {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptedTerms: boolean;
  registrationCode: string | null;
  language: string;
}

export interface PublicRegistrationConfig {
  registrationEnabled: boolean;
  hasUsableRegistrationKeys: boolean;
}

/**
 * Publieke account-flows: registratie, e-mailverificatie en password recovery.
 * Wrapt de gegenereerde platform-api client en mapt fouten naar ErrorInformation.
 */
@Injectable({ providedIn: 'root' })
export class AccountPublicApiService {
  private readonly api = inject(PlatformUserAccountService);
  private readonly errorMapper = inject(UpamErrorMapper);

  async isRegistrationEnabled(): AsyncResult<boolean> {
    const result = await firstValueFrom(this.api.getUseraccountsRegistrationConfig());
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(result.value?.registrationEnabled === true);
  }

  async getRegistrationConfig(): AsyncResult<PublicRegistrationConfig> {
    const result = await firstValueFrom(this.api.getUseraccountsRegistrationConfig());
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok({
      registrationEnabled: result.value?.registrationEnabled === true,
      hasUsableRegistrationKeys: result.value?.hasUsableRegistrationKeys === true
    });
  }

  async register(registration: PublicRegistration): AsyncResult<boolean> {
    const body: PlatformRegisterUserAccount = {
      firstName: registration.firstName,
      lastName: registration.lastName,
      displayName: `${registration.firstName} ${registration.lastName}`.trim(),
      email: registration.email,
      password: registration.password,
      confirmPassword: registration.confirmPassword,
      acceptedTerms: registration.acceptedTerms,
      registrationCode: registration.registrationCode,
      language: registration.language,
      accountCreatedByMethod: registration.registrationCode
        ? PlatformAccountCreatedByMethod.UserViaRegistrationKey
        : PlatformAccountCreatedByMethod.UserViaRegistration
    };
    const result = await firstValueFrom(this.api.postUseraccountsActionRegister({ body }));
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(true);
  }

  async verify(token: string): AsyncResult<boolean> {
    const result = await firstValueFrom(this.api.postUseraccountsActionVerify({ body: { token } }));
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(result.value === true);
  }

  async resendVerification(email: string, language: string): AsyncResult<boolean> {
    const result = await firstValueFrom(
      this.api.postUseraccountsActionResendVerification({ body: { email, language } })
    );
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(true);
  }

  async forgotPassword(email: string, language: string): AsyncResult<boolean> {
    const result = await firstValueFrom(
      this.api.postUseraccountsActionForgotpassword({ body: { email, language } })
    );
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(true);
  }

  async resetPassword(resetToken: string, password: string, confirmPassword: string): AsyncResult<boolean> {
    const result = await firstValueFrom(
      this.api.postUseraccountsActionResetpassword({ body: { resetToken, password, confirmPassword } })
    );
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(result.value === true);
  }
}
